import { createRoot } from 'react-dom/client';
import App from './App';

import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { store } from './app/store';
import './index.css';
import { ContextProviders } from './providers';

let errorLink = undefined;
if (process.env.REACT_APP_ENVIRONMENT !== 'local') {
    const strippedBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/(^\w+:|^)\/\//, '');
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        tracePropagationTargets: [strippedBaseUrl],
        release: process.env.REACT_APP_CURRENT_VERSION,
        tracesSampleRate: process.env.REACT_APP_ENVIRONMENT === 'production' ? 0.1 : 0.2,
        environment: process.env.REACT_APP_ENVIRONMENT,
    });

    errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
            graphQLErrors.map(({ message, locations, path }) => Sentry.captureMessage(message));
        if (networkError) {
            Sentry.captureException(networkError);
        }
    });
}

//Construct the content security policy string
const contentSecurityPolicy = `
default-src 'self' ${process.env.REACT_APP_API_BASE_URL} https://cdn.embedly.com https://*.squarecdn.com;
script-src 'self' https://*.zendesk.com https://*.zdassets.com https://*.sentry.io https://*.posthog.com https://*.google-analytics.com https://www.youtube.com https://*.squarecdn.com ${process.env.REACT_APP_API_BASE_URL} ${process.env.REACT_APP_PUBLIC_POSTHOG_HOST} https://*.afterpay.com https://pay.google.com/gp/p/js/pay.js;
connect-src 'self' wss://*.zendesk.com https://*.zendesk.com https://*.zdassets.com https://*.doubleclick.net https://*.analytics.google.com https://analytics.google.com https://*.squarecdn.com https://*.squareup.com https://*.squareupsandbox.com https://*.sentry.io https://*.posthog.com https://*.google-analytics.com https://*.amazonaws.com https://o160250.ingest.sentry.io https://*.afterpay.com https://google.com/pay ${process.env.REACT_APP_API_BASE_URL} ${process.env.REACT_APP_PUBLIC_POSTHOG_HOST} ${process.env.REACT_APP_HEALTHIE_ENDPOINT};
font-src 'self' data: https://fonts.gstatic.com https://fonts.googleapis.com https://square-fonts-production-f.squarecdn.com https://d1g145x70srn7h.cloudfront.net https://*.typekit.net;
style-src 'self' 'unsafe-inline' https://*.posthog.com https://*.typekit.net https://*.googleapis.com ${process.env.REACT_APP_API_BASE_URL} https://*.squarecdn.com https://fonts.googleapis.com;
img-src 'self' data: https://*.ytimg.com https://*.zendesk.com https://*.amazonaws.com https://*.webflow.com https://cdn.prod.website-files.com https://placedog.net https://*.youtube.com https://*.afterpay.com https://www.gstatic.com https://*.squarecdn.com https://*.gravatar.com;
frame-src 'self' https://pay.google.com https://*.squarecdn.com https://www.youtube.com https://cdn.embedly.com/;
`;

const links = errorLink
    ? [errorLink, new HttpLink({ uri: process.env.REACT_APP_HEALTHIE_ENDPOINT })]
    : [new HttpLink({ uri: process.env.REACT_APP_HEALTHIE_ENDPOINT })];

const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: from(links),
});

const params = new URLSearchParams(window.location.search);
const postHogDistinctId = params.get('ph_distinct_id');
const postHogSessionId = params.get('ph_session_id');

if (process.env.REACT_APP_PUBLIC_POSTHOG_KEY) {
    if (postHogDistinctId || postHogSessionId) {
        posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
            api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
            bootstrap: {
                distinctID: postHogDistinctId,
                sessionID: postHogSessionId,
                isIdentifiedID: false,
            },
            loaded: (ph) => {
                posthog?.capture('Visited patient portal');
            },
        });
    } else {
        posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
            api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
            loaded: (ph) => {
                posthog?.capture('Visited patient portal');
            },
        });
    }
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <ApolloProvider client={apolloClient}>
        <Provider store={store}>
            <BrowserRouter>
                <PostHogProvider client={posthog}>
                    <ContextProviders>
                        <App />
                        <meta httpEquiv="Content-Security-Policy" content={contentSecurityPolicy} />
                        <ToastContainer autoClose={1500} />
                    </ContextProviders>
                </PostHogProvider>
            </BrowserRouter>
        </Provider>
    </ApolloProvider>,
);
